/**
 * Spacing Design Tokens
 *
 * Standard spacing scale for consistent spacing throughout the application.
 * Based on an 8px grid system (Material-UI default).
 *
 * Usage with MUI:
 * - theme.spacing(tokens.spacing.md / 8) // 16px = 2 spacing units
 * - sx={{ padding: tokens.spacing.md }} // Direct pixel value
 */

export const spacing = {
  /** 4px - Extra small spacing for tight layouts */
  xs: 4,

  /** 8px - Small spacing, 1 MUI spacing unit */
  sm: 8,

  /** 16px - Medium spacing, 2 MUI spacing units (most common) */
  md: 16,

  /** 24px - Large spacing, 3 MUI spacing units */
  lg: 24,

  /** 32px - Extra large spacing, 4 MUI spacing units */
  xl: 32,

  /** 48px - 2X large spacing, 6 MUI spacing units */
  '2xl': 48,

  /** 64px - 3X large spacing, 8 MUI spacing units */
  '3xl': 64,
} as const;

/**
 * Semantic spacing tokens for specific use cases
 */
export const semanticSpacing = {
  /** Standard gap between cards in a grid */
  cardGap: spacing.md,

  /** Standard padding inside cards */
  cardPadding: spacing.lg,

  /** Standard margin for sections */
  sectionMargin: spacing.xl,

  /** Spacing between form fields */
  formFieldGap: spacing.md,

  /** Padding for containers */
  containerPadding: spacing.lg,

  /** Gap between icon and text */
  iconTextGap: spacing.sm,
} as const;

export type SpacingKey = keyof typeof spacing;
export type SemanticSpacingKey = keyof typeof semanticSpacing;
